import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import {
  IThemeConfig,
  LOCAL_STORAGE_LOGO,
  LOCAL_STORAGE_THEME,
  LOCAL_STORAGE_THEME_CONFIG,
  ThemeType,
  UiState,
} from '../ui.types'

const initialState: UiState = {
  isGlobalLoading: false,
  organizationLogo: null,
  currentThemeName: ThemeType.IDG,
  currentThemeConfig: null,
  themeError: null,
}

const getStoredTheme = (): ThemeType => {
  const storedTheme = localStorage.getItem(LOCAL_STORAGE_THEME)
  if (storedTheme) {
    try {
      return JSON.parse(storedTheme) as ThemeType
    } catch (e) {
      return ThemeType.IDG
    }
  }
  return ThemeType.IDG
}

const getStoredThemeConfig = (): IThemeConfig | null => {
  const storedConfig = localStorage.getItem(LOCAL_STORAGE_THEME_CONFIG)
  if (storedConfig) {
    try {
      return JSON.parse(storedConfig) as IThemeConfig
    } catch (e) {
      return null
    }
  }
  return null
}

const getStoredLogo = (): string | null => {
  const storedLogo = localStorage.getItem(LOCAL_STORAGE_LOGO)
  if (storedLogo) {
    try {
      return JSON.parse(storedLogo) as string
    } catch (e) {
      return null
    }
  }
  return null
}

const uiStore = createSlice({
  name: 'ui',
  initialState: {
    ...initialState,
    currentTheme: getStoredTheme(),
    currentThemeConfig: getStoredThemeConfig(),
    organizationLogo: getStoredLogo(),
  },
  reducers: {
    setGlobalIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isGlobalLoading = action.payload
    },
    setCurrentTheme: (state, action: PayloadAction<ThemeType>) => {
      state.currentTheme = action.payload
      localStorage.setItem(LOCAL_STORAGE_THEME, JSON.stringify(action.payload))
    },
    setThemeConfig: (state, action: PayloadAction<IThemeConfig>) => {
      state.currentThemeConfig = action.payload
      localStorage.setItem(
        LOCAL_STORAGE_THEME_CONFIG,
        JSON.stringify(action.payload),
      )
    },
    setOrganizationLogo: (state, action: PayloadAction<string>) => {
      state.organizationLogo = action.payload
      if (action.payload) {
        localStorage.setItem(LOCAL_STORAGE_LOGO, JSON.stringify(action.payload))
      } else {
        localStorage.removeItem(LOCAL_STORAGE_LOGO)
      }
    },
    setThemeError: (state, action: PayloadAction<string | null>) => {
      state.themeError = action.payload
    },
    destroyUILocalStore: (state) => {
      state.currentTheme = ThemeType.IDG
      state.currentThemeConfig = null
      localStorage.removeItem(LOCAL_STORAGE_THEME)
      localStorage.removeItem(LOCAL_STORAGE_THEME_CONFIG)
    },
  },
})

export const {
  setGlobalIsLoading,
  setCurrentTheme,
  setThemeConfig,
  setOrganizationLogo,
  setThemeError,
  destroyUILocalStore,
} = uiStore.actions

export default uiStore.reducer
